import React, { useState, useEffect, useCallback } from 'react';
import axios, { CancelToken } from 'axios';
import { Button } from '@mui/material';
import { moduleClientFactory } from '../../../api-service/module-client-factory';
import { LookupAutocomplete } from '../components/lookup-autocomplete';
import { IOfferLookupProps } from './interfaces';
import { GetOfferLookupOptionsRequest, OfferLookupDto } from '../../../api-service/clients';
import { formatOffer, renderOffer } from './formatters';
import { ReadonlyInput } from '../../readonly-input/readonly-input';
import { useFeatureToggles } from '../../../feature-toggles/feature-toggles';
import { useLookupOptions } from '../components/use-lookup-options';

const lookupClient = moduleClientFactory.createLookupOfferClient();

export function OfferLookup(props: IOfferLookupProps) {
  const {
    onChange,
    value,
    fetchMinimumInputLength = 2,
    label = 'Tilbud',
    placeholder,
    readOnly,
    disabled,
    helperText,
    noOptionsText,
    error,
    errorText,
    throttleMs = 400,
    excludeOffersWithExceededDeadline,
    validOfferIds,
    companyId,
    allowedCategories,
    ...rest
  } = props;
  const [selectedValue, setSelectedValue] = useState<OfferLookupDto>({} as OfferLookupDto);
  const [isLoading, setIsLoading] = useState(false);
  const { isFeatureEnabled } = useFeatureToggles();
  const suggestionsEnabled = isFeatureEnabled('EnableOfferLookupSuggestions');
  const filterKey = JSON.stringify({ validOfferIds, companyId, allowedCategories, excludeOffersWithExceededDeadline });
  const fetchOptions = useCallback(
    async function fetchOptions(query: string, cancelToken: CancelToken) {
      const filters: GetOfferLookupOptionsRequest = JSON.parse(filterKey);
      if (suggestionsEnabled) {
        const response = await lookupClient.getOfferLookupOptions({ ...filters, query }, cancelToken);
        return { options: response.offers || [], hasMore: response.hasMore };
      }
      const response = await lookupClient.getOffersByLookupQuery({ query, validOfferIds: filters.validOfferIds, companyId: filters.companyId }, cancelToken);
      return { options: response.offers || [] };
    },
    [filterKey, suggestionsEnabled]
  );
  const lookup = useLookupOptions({
    fetchOptions,
    enabled: !readOnly && !disabled,
    suggestionsEnabled,
    fetchMinimumInputLength,
    debounceMs: throttleMs,
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    let current = true;
    if (!value) {
      setSelectedValue({} as OfferLookupDto);
      setIsLoading(false);
      return undefined;
    }
    setIsLoading(true);
    lookupClient
      .getOfferById({ offerId: value }, source.token)
      .then(response => {
        if (current) setSelectedValue(response.offer || ({} as OfferLookupDto));
      })
      .catch(error => {
        if (!axios.isCancel(error) && current) setSelectedValue({} as OfferLookupDto);
      })
      .finally(() => {
        if (current) setIsLoading(false);
      });
    return () => {
      current = false;
      source.cancel();
    };
  }, [value]);

  function handleSelectOffer(selected: OfferLookupDto) {
    onChange?.(selected);
  }

  function filterOffers(queriedData: OfferLookupDto[]): OfferLookupDto[] {
    let result = queriedData;
    if (excludeOffersWithExceededDeadline === true) result = result.filter(x => x.deadlineForPlacementExceeded === false);
    if (allowedCategories?.length) result = result.filter(o => allowedCategories.includes(o.category?.id as number));
    return result;
  }

  function handleOpenOffer() {
    window.open(`/offer-and-activity/view-offer/${selectedValue.offerId}`);
  }

  const lookupError = (
    <>
      Kunne ikke hente tilbud.{' '}
      <Button size="small" onMouseDown={event => event.preventDefault()} onClick={lookup.handleRetry}>
        Prøv igen
      </Button>
    </>
  );
  const emptyText = lookup.hasError
    ? 'Kunne ikke hente tilbud'
    : lookup.searchValue.length > 0 && lookup.searchValue.length < fetchMinimumInputLength
    ? `Skriv mindst ${fetchMinimumInputLength} tegn for at søge`
    : suggestionsEnabled && !lookup.searchValue
    ? 'Ingen tilbud kan vælges'
    : undefined;

  return readOnly ? (
    <ReadonlyInput label={label} value={formatOffer(selectedValue)} />
  ) : (
    <LookupAutocomplete<OfferLookupDto>
      {...rest}
      label={label}
      placeholder={placeholder ?? (suggestionsEnabled ? 'Vælg eller søg efter tilbud' : 'Fremsøg tilbud')}
      isOptionEqualToValue={(option, val) => option?.offerId === val?.offerId}
      getOptionLabel={formatOffer}
      renderOption={renderOffer}
      onChange={handleSelectOffer}
      value={selectedValue}
      onSearchValueChange={lookup.handleSearch}
      onOpen={lookup.handleOpen}
      onClose={lookup.handleClose}
      openOnFocus={suggestionsEnabled}
      throttleMs={0}
      fetchMinimumInputLength={fetchMinimumInputLength}
      disabled={disabled}
      data={suggestionsEnabled ? lookup.options : filterOffers(lookup.options)}
      isLoading={lookup.isLoading || isLoading}
      error={error || lookup.hasError}
      errorText={lookup.hasError ? lookupError : errorText}
      helperText={lookup.hasMore && !lookup.searchValue ? <>Skriv for at søge efter flere tilbud. {helperText}</> : helperText}
      noOptionsText={noOptionsText ?? emptyText}
      onOpenEntity={selectedValue.offerId ? handleOpenOffer : undefined}
    />
  );
}
